import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AdminSidebar from "@/pages/admin/components/AdminSidebar";
import { getSupabase } from "@/lib/supabase";

interface HackerListing {
  id: number;
  profile_id: string;
  name: string;
  specialty: string | null;
  avatar: string | null;
  rating: number | null;
  rate: string | null;
  experience: string | null;
  age: number | null;
  gender: string | null;
  skills: string[];
  country: string | null;
  prizes: string[];
  certifications: string[];
  availability: string | null;
  ranking: number | null;
  verified: boolean;
  featured: boolean;
}

export default function AdminHackersPage() {
  const [hackers, setHackers] = useState<HackerListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingHacker, setEditingHacker] = useState<HackerListing | null>(null);
  const [editForm, setEditForm] = useState({
    name: "", specialty: "", rating: 0, rate: "", experience: "",
    country: "", avatar: "", gender: "", age: 0,
    ranking: 0, verified: false, featured: false, availability: "", skills: "", prizes: "", certifications: "",
  });
  const [saveStatus, setSaveStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [saveError, setSaveError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  // Create state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "", specialty: "", rating: 0, rate: "", experience: "",
    country: "", avatar: "", gender: "", age: 0,
    ranking: 0, verified: false, featured: false, availability: "", skills: "", prizes: "", certifications: "",
  });
  const [createStatus, setCreateStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [createError, setCreateError] = useState("");
  const [createPhotoUploading, setCreatePhotoUploading] = useState(false);
  const [createPhotoError, setCreatePhotoError] = useState("");

  // Bulk action state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [bulkRankValue, setBulkRankValue] = useState(0);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  useEffect(() => {
    fetchHackers();
  }, []);

  const fetchHackers = async () => {
    setLoading(true);
    try {
      const supabase = getSupabase();
      const { data } = await supabase.from("hacker_listings").select("*").order("ranking", { ascending: false });
      setHackers(data || []);
    } catch {
      // fallback
    } finally {
      setLoading(false);
    }
  };

  // --- Bulk selection handlers ---
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredHackers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredHackers.map((h) => h.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // --- Bulk actions ---
  const handleBulkVerify = async (verified: boolean) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkLoading(true);
    setBulkStatus("loading");
    try {
      const supabase = getSupabase();
      const { error } = await supabase
        .from("hacker_listings")
        .update({ verified })
        .in("id", ids);
      if (error) throw error;
      setHackers((prev) =>
        prev.map((h) => (ids.includes(h.id) ? { ...h, verified } : h))
      );
      setBulkStatus("success");
      clearSelection();
      setTimeout(() => setBulkStatus("idle"), 2000);
    } catch {
      setBulkStatus("error");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkRerank = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkLoading(true);
    setBulkStatus("loading");
    try {
      const supabase = getSupabase();
      const { error } = await supabase
        .from("hacker_listings")
        .update({ ranking: bulkRankValue })
        .in("id", ids);
      if (error) throw error;
      setHackers((prev) =>
        prev.map((h) => (ids.includes(h.id) ? { ...h, ranking: bulkRankValue } : h))
      );
      setBulkStatus("success");
      setBulkActionOpen(false);
      clearSelection();
      setTimeout(() => setBulkStatus("idle"), 2000);
    } catch {
      setBulkStatus("error");
    } finally {
      setBulkLoading(false);
    }
  };

  // --- Single hacker actions ---
  const openEdit = (h: HackerListing) => {
    setEditingHacker(h);
    setEditForm({
      name: h.name || "",
      specialty: h.specialty || "",
      rating: h.rating || 0,
      rate: h.rate || "",
      experience: h.experience || "",
      country: h.country || "",
      avatar: h.avatar || "",
      gender: h.gender || "",
      age: h.age || 0,
      ranking: h.ranking || 0,
      verified: h.verified || false,
      featured: h.featured || false,
      availability: h.availability || "",
      skills: (h.skills || []).join(", "),
      prizes: (h.prizes || []).join(", "),
      certifications: (h.certifications || []).join(", "),
    });
    setSaveStatus("idle");
    setSaveError("");
    setPhotoError("");
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      setPhotoError("Only JPG, PNG, WebP, or GIF images are allowed");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setPhotoError("Image must be under 5MB");
      return;
    }

    setPhotoUploading(true);
    setPhotoError("");

    try {
      const supabase = getSupabase();
      const fileExt = file.name.split(".").pop() || "jpg";
      const fileName = `hacker-${editingHacker?.id || Date.now()}-${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      if (urlData?.publicUrl) {
        setEditForm((prev) => ({ ...prev, avatar: urlData.publicUrl }));
      }
    } catch (err: unknown) {
      setPhotoError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const handleCreatePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      setCreatePhotoError("Only JPG, PNG, WebP, or GIF images are allowed");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setCreatePhotoError("Image must be under 5MB");
      return;
    }

    setCreatePhotoUploading(true);
    setCreatePhotoError("");

    try {
      const supabase = getSupabase();
      const fileExt = file.name.split(".").pop() || "jpg";
      const fileName = `hacker-new-${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      if (urlData?.publicUrl) {
        setCreateForm((prev) => ({ ...prev, avatar: urlData.publicUrl }));
      }
    } catch (err: unknown) {
      setCreatePhotoError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setCreatePhotoUploading(false);
      e.target.value = "";
    }
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) {
      setCreateError("Name is required");
      setCreateStatus("error");
      return;
    }
    setCreateStatus("loading");
    setCreateError("");
    try {
      const supabase = getSupabase();
      const profileId = crypto.randomUUID();
      const { data, error } = await supabase
        .from("hacker_listings")
        .insert({
          profile_id: profileId,
          name: createForm.name,
          specialty: createForm.specialty || null,
          avatar: createForm.avatar || null,
          rating: createForm.rating || 0,
          rate: createForm.rate || null,
          experience: createForm.experience || null,
          country: createForm.country || null,
          gender: createForm.gender || null,
          age: createForm.age || null,
          ranking: createForm.ranking || 0,
          verified: createForm.verified || false,
          featured: createForm.featured || false,
          availability: createForm.availability || null,
          skills: createForm.skills.split(",").map((s) => s.trim()).filter(Boolean),
          prizes: createForm.prizes.split(",").map((s) => s.trim()).filter(Boolean),
          certifications: createForm.certifications.split(",").map((s) => s.trim()).filter(Boolean),
        })
        .select()
        .single();
      if (error) throw error;
      setCreateStatus("success");
      setHackers((prev) => [data, ...prev]);
      setTimeout(() => {
        setShowCreateModal(false);
        setCreateStatus("idle");
      }, 1200);
    } catch (err: unknown) {
      setCreateStatus("error");
      setCreateError(err instanceof Error ? err.message : "Failed to create hacker");
    }
  };

  const handleSave = async () => {
    if (!editingHacker) return;
    setSaveStatus("loading");
    setSaveError("");
    try {
      const supabase = getSupabase();
      const { error } = await supabase
        .from("hacker_listings")
        .update({
          name: editForm.name,
          specialty: editForm.specialty,
          avatar: editForm.avatar,
          rating: editForm.rating,
          rate: editForm.rate,
          experience: editForm.experience,
          country: editForm.country,
          gender: editForm.gender,
          age: editForm.age,
          ranking: editForm.ranking,
          verified: editForm.verified,
          featured: editForm.featured,
          availability: editForm.availability,
          skills: editForm.skills.split(",").map((s) => s.trim()).filter(Boolean),
          prizes: editForm.prizes.split(",").map((s) => s.trim()).filter(Boolean),
          certifications: editForm.certifications.split(",").map((s) => s.trim()).filter(Boolean),
        })
        .eq("id", editingHacker.id);
      if (error) throw error;
      setSaveStatus("success");
      setHackers((prev) =>
        prev.map((h) =>
          h.id === editingHacker.id
            ? {
                ...h,
                name: editForm.name,
                specialty: editForm.specialty,
                avatar: editForm.avatar,
                rating: editForm.rating,
                rate: editForm.rate,
                experience: editForm.experience,
                country: editForm.country,
                gender: editForm.gender,
                age: editForm.age,
                ranking: editForm.ranking,
                verified: editForm.verified,
                featured: editForm.featured,
                availability: editForm.availability,
                skills: editForm.skills.split(",").map((s) => s.trim()).filter(Boolean),
                prizes: editForm.prizes.split(",").map((s) => s.trim()).filter(Boolean),
                certifications: editForm.certifications.split(",").map((s) => s.trim()).filter(Boolean),
              }
            : h
        )
      );
      setTimeout(() => {
        setEditingHacker(null);
        setSaveStatus("idle");
      }, 1200);
    } catch (err: unknown) {
      setSaveStatus("error");
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const supabase = getSupabase();
      await supabase.from("hacker_listings").delete().eq("id", id);
      setHackers((prev) => prev.filter((h) => h.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setConfirmDeleteId(null);
    } catch {
      // fallback
    }
  };

  const handleToggleVerified = async (h: HackerListing) => {
    try {
      const supabase = getSupabase();
      const newVerified = !h.verified;
      await supabase.from("hacker_listings").update({ verified: newVerified }).eq("id", h.id);
      setHackers((prev) => prev.map((x) => (x.id === h.id ? { ...x, verified: newVerified } : x)));
    } catch {
      // fallback
    }
  };

  const handleToggleFeatured = async (h: HackerListing) => {
    try {
      const supabase = getSupabase();
      const newFeatured = !h.featured;
      await supabase.from("hacker_listings").update({ featured: newFeatured }).eq("id", h.id);
      setHackers((prev) => prev.map((x) => (x.id === h.id ? { ...x, featured: newFeatured } : x)));
    } catch {
      // fallback
    }
  };

  const filteredHackers = hackers.filter(
    (h) =>
      h.name.toLowerCase().includes(search.toLowerCase()) ||
      (h.specialty || "").toLowerCase().includes(search.toLowerCase()) ||
      (h.country || "").toLowerCase().includes(search.toLowerCase())
  );

  const selectedCount = selectedIds.size;

  return (
    <div className="min-h-screen bg-[#161b22]">
      <AdminSidebar />
      <div className="ml-60">
        <header className="bg-[#0d1117] border-b border-gray-700 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <div>
            <h1 className="text-lg font-bold text-gray-200">Hackers</h1>
            <p className="text-xs text-gray-500">{hackers.length} total hackers</p>
          </div>
          <div className="flex items-center gap-3">
            {bulkStatus === "success" && (
              <span className="text-xs font-semibold text-green-400 bg-green-900/20 px-3 py-1.5 rounded-full flex items-center gap-1.5 whitespace-nowrap">
                <i className="fas fa-check-circle text-xs" /> Bulk action completed
              </span>
            )}
            {bulkStatus === "error" && (
              <span className="text-xs font-semibold text-red-400 bg-red-900/20 px-3 py-1.5 rounded-full flex items-center gap-1.5 whitespace-nowrap">
                <i className="fas fa-exclamation-circle text-xs" /> Bulk action failed
              </span>
            )}
            <button
              onClick={() => {
                setCreateForm({ name: "", specialty: "", rating: 0, rate: "", experience: "", country: "", avatar: "", gender: "", age: 0, ranking: 0, verified: false, featured: false, availability: "", skills: "", prizes: "", certifications: "" });
                setCreateStatus("idle");
                setCreateError("");
                setCreatePhotoError("");
                setShowCreateModal(true);
              }}
              className="px-4 py-2 bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 whitespace-nowrap"
            >
              <i className="fas fa-plus text-[10px]" />Create New Hacker
            </button>
            <div className="relative">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search hackers..."
                className="pl-9 pr-4 py-2 border border-gray-700 rounded-lg text-xs w-56 focus:outline-none focus:border-[#5cb85c]"
              />
            </div>
          </div>
        </header>

        {/* Bulk Action Bar */}
        {selectedCount > 0 && (
          <div className="mx-6 mt-4 bg-[#1a1a2e] text-white rounded-xl px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold">{selectedCount} hacker{selectedCount > 1 ? "s" : ""} selected</span>
              <button onClick={clearSelection} className="text-xs text-gray-400 hover:text-white transition-colors whitespace-nowrap">
                <i className="fas fa-times mr-1" />Clear
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkVerify(true)}
                disabled={bulkLoading}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                <i className="fas fa-check-circle mr-1.5" />Verify All
              </button>
              <button
                onClick={() => handleBulkVerify(false)}
                disabled={bulkLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                <i className="fas fa-times-circle mr-1.5" />Unverify All
              </button>
              <button
                onClick={() => setBulkActionOpen(true)}
                disabled={bulkLoading}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                <i className="fas fa-sort-amount-down mr-1.5" />Re-rank
              </button>
            </div>
          </div>
        )}

        <main className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="fas fa-spinner fa-spin text-[#5cb85c] text-2xl" />
            </div>
          ) : (
            <div className="bg-[#0d1117] rounded-xl border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#161b22] border-b border-gray-800">
                      <th className="w-10 px-3 py-3">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={selectedCount === filteredHackers.length && filteredHackers.length > 0}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-gray-600 text-[#5cb85c] focus:ring-[#5cb85c] cursor-pointer"
                          />
                        </div>
                      </th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Hacker</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Specialty</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Rating</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Rank</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Featured</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Verified</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase w-40">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredHackers.map((h) => (
                      <tr key={h.id} className={`hover:bg-[#161b22]/50 ${selectedIds.has(h.id) ? "bg-[#5cb85c]/5" : ""} ${h.featured ? "border-l-2 border-l-[#f0ad4e]" : ""}`}>
                        <td className="px-3 py-3.5">
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(h.id)}
                              onChange={() => toggleSelect(h.id)}
                              className="w-4 h-4 rounded border-gray-600 text-[#5cb85c] focus:ring-[#5cb85c] cursor-pointer"
                            />
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            {h.avatar ? (
                              <img
                                src={h.avatar}
                                alt={h.name}
                                className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove("hidden");
                                }}
                              />
                            ) : null}
                            <div className={`w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xs font-bold flex-shrink-0 ${h.avatar ? "hidden" : ""}`}>
                              {h.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-200">
                                {h.verified && (
                                  <i className="fas fa-check-circle text-[#5bc0de] text-xs mr-1" />
                                )}
                                {h.name}
                                {h.featured && (
                                  <span className="ml-2 text-[10px] bg-[#f0ad4e]/20 text-[#f0ad4e] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide">Featured</span>
                                )}
                              </p>
                              <p className="text-[11px] text-gray-400">
                                {h.country || "—"}{h.gender ? ` · ${h.gender === "male" ? "M" : "F"}` : ""}{h.age ? `, ${h.age}` : ""}{h.rate ? ` · ${h.rate}` : ""}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className="text-xs text-gray-500">{h.specialty || "—"}</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-xs font-semibold text-amber-600">
                            <i className="fas fa-star text-[10px] mr-1" />{h.rating}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-xs font-semibold text-gray-300">#{h.ranking || 0}</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <button
                            onClick={() => handleToggleFeatured(h)}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all whitespace-nowrap ${
                              h.featured ? "bg-[#f0ad4e]/20 text-[#f0ad4e]" : "bg-[#1c2128] text-gray-400"
                            }`}
                          >
                            {h.featured ? "Yes" : "No"}
                          </button>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <button
                            onClick={() => handleToggleVerified(h)}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all whitespace-nowrap ${
                              h.verified ? "bg-green-900/30 text-green-400" : "bg-[#1c2128] text-gray-400"
                            }`}
                          >
                            {h.verified ? "Yes" : "No"}
                          </button>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/admin/hackers/${h.id}`}
                              className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400 hover:text-[#5bc0de] transition-all"
                              title="View Profile"
                            >
                              <i className="fas fa-eye text-xs" />
                            </Link>
                            <button
                              onClick={() => openEdit(h)}
                              className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400 hover:text-[#5cb85c] transition-all"
                              title="Edit"
                            >
                              <i className="fas fa-edit text-xs" />
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(h.id)}
                              className="w-7 h-7 rounded-lg hover:bg-red-900/20 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all"
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt text-xs" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredHackers.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-5 py-12 text-center text-sm text-gray-400">
                          No hackers found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Bulk Re-rank Modal */}
      {bulkActionOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setBulkActionOpen(false)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-200">Bulk Re-rank</h3>
              <button onClick={() => setBulkActionOpen(false)} className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Set ranking for <strong>{selectedCount}</strong> selected hacker{selectedCount > 1 ? "s" : ""}
            </p>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">New Ranking Value</label>
              <input
                type="number"
                min="0"
                value={bulkRankValue}
                onChange={(e) => setBulkRankValue(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]"
                placeholder="Enter ranking number..."
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setBulkActionOpen(false)}
                className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkRerank}
                disabled={bulkLoading}
                className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-700 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                {bulkLoading ? "Updating..." : "Apply Rank"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingHacker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8" onClick={() => setEditingHacker(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-lg mx-4 p-6 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-200">Edit Hacker</h3>
              <button onClick={() => setEditingHacker(null)} className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Name</label>
                <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Specialty</label>
                <input type="text" value={editForm.specialty} onChange={(e) => setEditForm({ ...editForm, specialty: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-2">Profile Photo</label>
                <div className="flex flex-col items-center gap-4">
                  <div
                    className="relative group cursor-pointer"
                    onClick={() => document.getElementById("hacker-photo-input")?.click()}
                  >
                    {editForm.avatar ? (
                      <img
                        src={editForm.avatar}
                        alt="Profile"
                        className="w-28 h-28 rounded-full object-cover border-2 border-gray-600 group-hover:border-[#5cb85c] transition-all"
                      />
                    ) : (
                      <div className="w-28 h-28 rounded-full bg-[#1c2128] border-2 border-dashed border-gray-600 group-hover:border-[#5cb85c] flex items-center justify-center transition-all">
                        <i className="fas fa-camera text-gray-500 text-2xl group-hover:text-[#5cb85c]" />
                      </div>
                    )}
                    <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-white text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
                        <i className="fas fa-camera text-sm" />Change Photo
                      </span>
                    </div>
                    {photoUploading && (
                      <div className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin text-[#5cb85c] text-xl" />
                      </div>
                    )}
                  </div>

                  <input
                    id="hacker-photo-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />

                  {photoError && (
                    <p className="text-xs text-red-400">{photoError}</p>
                  )}

                  <div className="w-full">
                    <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide">Or paste image URL</label>
                    <input
                      type="text"
                      value={editForm.avatar}
                      onChange={(e) => { setEditForm({ ...editForm, avatar: e.target.value }); setPhotoError(""); }}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-700 rounded-lg text-xs focus:outline-none focus:border-[#5cb85c]"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Rating (0-5)</label>
                <input type="number" min="0" max="5" step="0.1" value={editForm.rating} onChange={(e) => setEditForm({ ...editForm, rating: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Ranking</label>
                <input type="number" min="0" value={editForm.ranking} onChange={(e) => setEditForm({ ...editForm, ranking: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Rate</label>
                <input type="text" value={editForm.rate} onChange={(e) => setEditForm({ ...editForm, rate: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Experience</label>
                <input type="text" value={editForm.experience} onChange={(e) => setEditForm({ ...editForm, experience: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Country</label>
                <input type="text" value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Gender</label>
                <select value={editForm.gender} onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]">
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Age</label>
                <input type="number" min="18" max="99" value={editForm.age || ""} onChange={(e) => setEditForm({ ...editForm, age: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="e.g. 28" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Availability</label>
                <select value={editForm.availability} onChange={(e) => setEditForm({ ...editForm, availability: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]">
                  <option value="">Select...</option>
                  <option value="Available">Available</option>
                  <option value="Busy">Busy</option>
                  <option value="Unavailable">Unavailable</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-1">Skills (comma-separated)</label>
                <input type="text" value={editForm.skills} onChange={(e) => setEditForm({ ...editForm, skills: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-1">Prizes (comma-separated)</label>
                <input type="text" value={editForm.prizes} onChange={(e) => setEditForm({ ...editForm, prizes: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-1">Certifications (comma-separated)</label>
                <input type="text" value={editForm.certifications} onChange={(e) => setEditForm({ ...editForm, certifications: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div className="col-span-2 flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editForm.verified} onChange={(e) => setEditForm({ ...editForm, verified: e.target.checked })} className="text-[#5cb85c] rounded" />
                  <span className="text-xs font-semibold text-gray-400">Verified Hacker</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editForm.featured} onChange={(e) => setEditForm({ ...editForm, featured: e.target.checked })} className="text-[#f0ad4e] rounded" />
                  <span className="text-xs font-semibold text-gray-400">Featured Hacker</span>
                </label>
              </div>
            </div>
            {saveStatus === "error" && <p className="mt-3 text-xs text-red-500">{saveError}</p>}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditingHacker(null)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">Cancel</button>
              <button onClick={handleSave} disabled={saveStatus === "loading"} className="flex-1 px-4 py-2.5 bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">
                {saveStatus === "loading" ? "Saving..." : saveStatus === "success" ? "Saved!" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-200 text-center mb-2">Delete Hacker?</h3>
            <p className="text-xs text-gray-500 text-center mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">Cancel</button>
              <button onClick={() => handleDelete(confirmDeleteId)} className="flex-1 px-4 py-2.5 bg-red-900/200 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Hacker Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8" onClick={() => setShowCreateModal(false)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-lg mx-4 p-6 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-200">Create New Hacker</h3>
              <button onClick={() => setShowCreateModal(false)} className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Name *</label>
                <input type="text" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="Full name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Specialty</label>
                <input type="text" value={createForm.specialty} onChange={(e) => setCreateForm({ ...createForm, specialty: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="e.g. Penetration Testing" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-2">Profile Photo</label>
                <div className="flex flex-col items-center gap-4">
                  <div
                    className="relative group cursor-pointer"
                    onClick={() => document.getElementById("create-hacker-photo-input")?.click()}
                  >
                    {createForm.avatar ? (
                      <img
                        src={createForm.avatar}
                        alt="Profile"
                        className="w-28 h-28 rounded-full object-cover border-2 border-gray-600 group-hover:border-[#5cb85c] transition-all"
                      />
                    ) : (
                      <div className="w-28 h-28 rounded-full bg-[#1c2128] border-2 border-dashed border-gray-600 group-hover:border-[#5cb85c] flex items-center justify-center transition-all">
                        <i className="fas fa-camera text-gray-500 text-2xl group-hover:text-[#5cb85c]" />
                      </div>
                    )}
                    <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-white text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
                        <i className="fas fa-camera text-sm" />Upload Photo
                      </span>
                    </div>
                    {createPhotoUploading && (
                      <div className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin text-[#5cb85c] text-xl" />
                      </div>
                    )}
                  </div>
                  <input
                    id="create-hacker-photo-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleCreatePhotoUpload}
                    className="hidden"
                  />
                  {createPhotoError && (
                    <p className="text-xs text-red-400">{createPhotoError}</p>
                  )}
                  <div className="w-full">
                    <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide">Or paste image URL</label>
                    <input
                      type="text"
                      value={createForm.avatar}
                      onChange={(e) => { setCreateForm({ ...createForm, avatar: e.target.value }); setCreatePhotoError(""); }}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-700 rounded-lg text-xs focus:outline-none focus:border-[#5cb85c]"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Rating (0-5)</label>
                <input type="number" min="0" max="5" step="0.1" value={createForm.rating} onChange={(e) => setCreateForm({ ...createForm, rating: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Ranking</label>
                <input type="number" min="0" value={createForm.ranking} onChange={(e) => setCreateForm({ ...createForm, ranking: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Rate</label>
                <input type="text" value={createForm.rate} onChange={(e) => setCreateForm({ ...createForm, rate: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="e.g. $120/hr" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Experience</label>
                <input type="text" value={createForm.experience} onChange={(e) => setCreateForm({ ...createForm, experience: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="e.g. 5+ years" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Country</label>
                <input type="text" value={createForm.country} onChange={(e) => setCreateForm({ ...createForm, country: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="e.g. USA" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Gender</label>
                <select value={createForm.gender} onChange={(e) => setCreateForm({ ...createForm, gender: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]">
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Age</label>
                <input type="number" min="18" max="99" value={createForm.age || ""} onChange={(e) => setCreateForm({ ...createForm, age: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="e.g. 28" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Availability</label>
                <select value={createForm.availability} onChange={(e) => setCreateForm({ ...createForm, availability: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]">
                  <option value="">Select...</option>
                  <option value="Available">Available</option>
                  <option value="Busy">Busy</option>
                  <option value="Unavailable">Unavailable</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-1">Skills (comma-separated)</label>
                <input type="text" value={createForm.skills} onChange={(e) => setCreateForm({ ...createForm, skills: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="e.g. Python, SQL Injection, Wireshark" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-1">Prizes (comma-separated)</label>
                <input type="text" value={createForm.prizes} onChange={(e) => setCreateForm({ ...createForm, prizes: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="e.g. DEF CON CTF 2024, HackTheBox Pro" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-400 mb-1">Certifications (comma-separated)</label>
                <input type="text" value={createForm.certifications} onChange={(e) => setCreateForm({ ...createForm, certifications: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]" placeholder="e.g. OSCP, CISSP, CEH" />
              </div>
              <div className="col-span-2 flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={createForm.verified} onChange={(e) => setCreateForm({ ...createForm, verified: e.target.checked })} className="text-[#5cb85c] rounded" />
                  <span className="text-xs font-semibold text-gray-400">Verified Hacker</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={createForm.featured} onChange={(e) => setCreateForm({ ...createForm, featured: e.target.checked })} className="text-[#f0ad4e] rounded" />
                  <span className="text-xs font-semibold text-gray-400">Featured Hacker</span>
                </label>
              </div>
            </div>
            {createStatus === "error" && <p className="mt-3 text-xs text-red-500">{createError}</p>}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">Cancel</button>
              <button onClick={handleCreate} disabled={createStatus === "loading"} className="flex-1 px-4 py-2.5 bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">
                {createStatus === "loading" ? "Creating..." : createStatus === "success" ? "Created!" : "Create Hacker"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}